import { captureEvent } from "@/app/_analytics/analytics";
import { env } from "@/env";
import { EventName } from "@mw/shared/analyticsEventNames";

export type MusicGenerationError = "generate_aborted" | "generate_failed" | "quota_exceeded";

export interface Track {
  id: string;
  src: string;
  title: string;
  duration: number | null;
}

export async function generateMusic({
  prompt,
  signal,
  source,
}: {
  prompt: string;
  signal?: AbortSignal;
  source?: string;
}): Promise<[error: MusicGenerationError, track: null] | [error: null, track: Track]> {
  try {
    const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/music/detailed/anonymous`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ prompt }),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      if (errorData?.detail?.status === "quota_exceeded") {
        captureEvent(EventName.marketing_website_music_demo_quota_exceeded, { prompt, source });
        return ["quota_exceeded", null];
      }
      captureEvent(EventName.marketing_website_music_demo_error, { prompt, source, error: errorData?.detail?.message });
      return ["generate_failed", null];
    }

    const data = await response.json();

    captureEvent(EventName.marketing_website_music_demo_generate, { prompt, source });

    return [
      null,
      {
        id: data.id,
        src: data.download_url,
        title: data.metadata.title,
        duration: null,
      },
    ];
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return ["generate_aborted", null];
    }
    captureEvent(EventName.marketing_website_music_demo_error, {
      prompt,
      source,
      error: error instanceof Error ? error.message : "Unknown error",
    });
    return ["generate_failed", null];
  }
}
